import apiClient from "@/axiosConfig";
import { useUxStore } from "@/stores/uxStore";
import { extractApiErrorMessage } from "@/utils/apiErrorUtils";

const getUxStore = () => useUxStore();

export default {
  async getUsers(search = null, page = 1, pageSize = 50) {
    try {
      getUxStore().startLoading();
      const params = { page, pageSize };
      if (search) params.search = search;
      return await apiClient.get("/admin/users", { params });
    } catch (error) {
      console.error("Error in AdminUserService.getUsers:", error);
      getUxStore().setError("Fehler beim Laden der Benutzer.");
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },

  async createUser(payload, idempotencyKey = null) {
    try {
      getUxStore().startLoading();
      const config = idempotencyKey ? { headers: { "Idempotency-Key": idempotencyKey } } : undefined;
      return await apiClient.post("/admin/users", payload, config);
    } catch (error) {
      console.error("Error in AdminUserService.createUser:", error);
      getUxStore().setError(extractApiErrorMessage(error, "Fehler beim Einladen des Benutzers."));
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },
  async getProvisioningStatus(id) {
    return await apiClient.get(`/admin/users/provisioning/${id}`);
  },
  async retryProvisioning(id) {
    return await apiClient.post(`/admin/users/provisioning/${id}/retry`);
  },
  async resendInvitation(id) {
    return await apiClient.post(`/admin/users/provisioning/${id}/resend-invitation`);
  },
  async getUserById(id) {
    try {
      getUxStore().startLoading();
      return await apiClient.get(`/admin/users/${id}`);
    } catch (error) {
      console.error("Error in AdminUserService.getUserById:", error);
      getUxStore().setError("Fehler beim Laden des Benutzers.");
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },

  async updateUserProfile(id, payload) {
    try {
      getUxStore().startLoading();
      return await apiClient.put(`/admin/users/${id}/profile`, payload);
    } catch (error) {
      console.error("Error in AdminUserService.updateUserProfile:", error);
      getUxStore().setError(extractApiErrorMessage(error, "Fehler beim Speichern des Benutzers."));
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },
  async setUserActiveStatus(id, isActive) {
    try {
      getUxStore().startLoading();
      return await apiClient.put(`/admin/users/${id}/active`, { isActive: Boolean(isActive) });
    } catch (error) {
      console.error("Error in AdminUserService.setUserActiveStatus:", error);
      getUxStore().setError("Fehler beim Speichern des Benutzerstatus.");
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },
  async setUserCompanies(id, companyIds) {
    try {
      getUxStore().startLoading();
      return await apiClient.put(`/admin/users/${id}/companies`, { ids: companyIds || [] });
    } catch (error) {
      console.error("Error in AdminUserService.setUserCompanies:", error);
      getUxStore().setError("Fehler beim Speichern der Firmenzuordnung.");
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },

  async setUserGlobalRoles(id, roleIds) {
    try {
      getUxStore().startLoading();
      return await apiClient.put(`/admin/users/${id}/roles/global`, { ids: roleIds || [] });
    } catch (error) {
      console.error("Error in AdminUserService.setUserGlobalRoles:", error);
      getUxStore().setError("Fehler beim Speichern der globalen Rollen.");
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },

  async setUserCompanyRoles(id, companyId, roleIds) {
    try {
      getUxStore().startLoading();
      return await apiClient.put(`/admin/users/${id}/roles/company/${companyId}`, { ids: roleIds || [] });
    } catch (error) {
      console.error("Error in AdminUserService.setUserCompanyRoles:", error);
      getUxStore().setError("Fehler beim Speichern der Firmenrollen.");
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },

  async addExternalIdentity(id, identityProviderId, subject) {
    try {
      getUxStore().startLoading();
      return await apiClient.post(`/admin/users/${id}/external-identities`, { identityProviderId, subject });
    } catch (error) {
      console.error("Error in AdminUserService.addExternalIdentity:", error);
      getUxStore().setError("Fehler beim Hinzufügen der externen Identität.");
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },

  async removeExternalIdentity(externalIdentityId) {
    try {
      getUxStore().startLoading();
      return await apiClient.delete(`/admin/users/external-identities/${externalIdentityId}`);
    } catch (error) {
      console.error("Error in AdminUserService.removeExternalIdentity:", error);
      getUxStore().setError("Fehler beim Entfernen der externen Identität.");
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },
};


